import {
  useSavedReport,
  useUpdateReport,
} from '@features/reporting/hooks/useSavedReports'
import { toast } from '@lib/toasts'
import { Spinner } from 'components/loaders/Spinner'
import { Trans, useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { ReportTypeMap, SavedReport } from '@features/reporting/types'
import { UseQueryResult } from '@tanstack/react-query'
import { Button, Switch } from 'components/buttons'
import { faCopy, faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { faCopy as faCopySolid, faKey } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReportFilter } from '@lib/api'
import { Sorted } from '@features/reporting/hooks/useSort'
import { toGroupByParams } from '../../utils/toGroupByParams'
import { toApiParams } from '../../utils/toApiParams'
import { GroupByValue } from '@features/reporting/types/groupByTypes'

/**
 * Allows enabling/disabling API access for a time log report.
 */
export const TimeLogReportApiUrl = <RT extends keyof ReportTypeMap>({
  columnIds,
  filterParams,
  groupBy,
  reportId,
  sorted,
  type,
}: {
  columnIds: ReportTypeMap[RT]['columnId'][]
  filterParams: ReportFilter<RT>
  groupBy?: GroupByValue
  reportId: number
  sorted: Sorted
  type: RT
}) => {
  const { t } = useTranslation()

  const updateReport = useUpdateReport()

  const updateApiAccess = async (apiAccessEnabled: boolean) => {
    try {
      await updateReport.mutateAsync({
        apiAccessEnabled,
        id: reportId,
        /**
         * By sending in these properties, we can effectively migrate saved
         * reports to be ready for API access.
         */
        ...toApiParams({ type, columnIds, filterParams, sorted }),
        ...toGroupByParams(groupBy),
      })
      if (!apiAccessEnabled) {
        toast({
          content: t(
            'features.reporting.enableApiAccess.youHaveDisabledApiAccess',
          ),
          title: t('features.reporting.enableApiAccess.apiAccessDisabled'),
          variant: 'success',
        })
      } else {
        toast({
          content: t(
            'features.reporting.enableApiAccess.youCanNowAccessReportUsingYourSavedApiKey',
          ),
          title: t('features.reporting.enableApiAccess.apiAccessEnabled'),
          variant: 'success',
        })
      }
    } catch (error) {
      Sentry.captureException(error)

      toast({
        content: t('features.reporting.enableApiAccess.error'),
        title: t('common.error'),
        variant: 'error',
      })
    }
  }

  const savedReport = useSavedReport(reportId) as UseQueryResult<
    SavedReport<'time_log'>
  >

  const handleCopy = async () => {
    const apiUrl = savedReport.data?.apiUrl
    if (!apiUrl) return

    await navigator.clipboard.writeText(apiUrl)
    toast({
      content: t('features.reporting.enableApiAccess.urlIsCopiedToClipboard'),
      title: t('features.reporting.enableApiAccess.copiedUrl'),
      variant: 'success',
    })
  }

  return (
    <div className="flex flex-col gap-4 items-start text-sm text-neutral-900 max-w-md overflow-hidden p-6 pt-0">
      <p className="text-left">
        <Trans
          i18nKey="features.reporting.enableApiAccess.description"
          values={{ name: savedReport.data?.name }}
          components={{ s: <strong /> }}
        />
      </p>
      <label className="flex flex-row items-center gap-2 pointer">
        <Switch
          checked={savedReport.data?.apiAccessEnabled}
          disabled={updateReport.isLoading || savedReport.isLoading}
          onChange={() => {
            void updateApiAccess(!savedReport.data?.apiAccessEnabled)
          }}
        />
        {savedReport.data?.apiAccessEnabled
          ? t('common.active')
          : t('common.inactive')}
        {(updateReport.isLoading || savedReport.isLoading) && <Spinner />}
      </label>
      {savedReport.data?.apiAccessEnabled && (
        <div className="flex flex-col gap-2 w-full items-start">
          <pre className="bg-neutral-100 rounded border border-neutral-200 p-4 w-full text-left break-all text-wrap">
            {savedReport.data?.apiUrl}
            <Button
              className="p-0 mt-2 mb-0 text-primary-600"
              variant="text"
              onClick={() => void handleCopy()}
            >
              <FontAwesomeIcon icon={faCopy} />
              {t('features.reporting.enableApiAccess.copyUrl')}
            </Button>
          </pre>
          <div className="grid grid-flow-row grid-auto-column grid-auto-columns-[auto_1fr] grid-rows-2 gap-4 items-center text-left">
            <FontAwesomeIcon
              icon={faCopySolid}
              className="col-start-1 row-start-1"
            />
            <span className="col-start-2 row-start-1">
              {t('features.reporting.enableApiAccess.copyUrlInstructions')}
            </span>
            <FontAwesomeIcon icon={faKey} className="col-start-1 row-start-2" />
            <span className="col-start-2 row-start-2">
              <Trans
                i18nKey="features.reporting.enableApiAccess.apiKeyInstructions"
                components={{ code: <code className="font-mono" /> }}
              />
            </span>
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="col-start-1 row-start-3"
            />
            <span className="col-start-2 row-start-3">
              <Trans
                i18nKey="features.reporting.enableApiAccess.queryParamInstructions"
                components={{ code: <code className="font-mono" /> }}
              />
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
